import { alpha } from "@mui/material/styles";

// ── Shared typography constants for consistent UI ──
export const microLabelSx = {
  fontSize: 10.5,
  fontWeight: 600,
  letterSpacing: 0.6,
  textTransform: "uppercase",
  lineHeight: 1.1,
};

export const panelTitleSx = {
  fontWeight: 600,
  letterSpacing: 0.2,
  fontSize: 14,
};

export const numericSx = {
  fontVariantNumeric: "tabular-nums",
  fontWeight: 600,
};

export const compactFieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: 1.25, height: 40 },
  "& .MuiInputLabel-root": { fontSize: 12.5, fontWeight: 700 },
  "& input, & .MuiSelect-select": {
    fontWeight: 700,
    fontSize: 13.5,
    fontVariantNumeric: "tabular-nums",
  },
};

export const tableHeadCellSx = (theme) => ({
  py: 0.7,
  px: 1,
  fontWeight: 600,
  fontSize: 10.5,
  letterSpacing: 0.7,
  textTransform: "uppercase",
  color: alpha(theme.palette.text.primary, 0.7),
  borderBottom: "1px solid",
  borderBottomColor: alpha(theme.palette.divider, 0.9),
  whiteSpace: "nowrap",
  bgcolor: theme.palette.mode === "dark"
    ? alpha(theme.palette.common.white, 0.04)
    : alpha(theme.palette.common.black, 0.025),
});

export const tableBodyCellSx = (theme) => ({
  py: 1,
  px: 1,
  fontSize: 13.5,
  fontVariantNumeric: "tabular-nums",
  borderBottom: "1px solid",
  borderBottomColor: alpha(theme.palette.divider, 0.7),
  whiteSpace: "nowrap",
});

const toneColor = (theme, tone = "neutral") => {
  switch (tone) {
    case "danger":
      return theme.palette.error.main;
    case "warning":
    case "accent":
      return theme.palette.warning.main;
    case "success":
      return theme.palette.success.main;
    default:
      return theme.palette.text.primary;
  }
};

export const tacticalSurface = (theme, raised = false) => ({
  backgroundColor: theme.palette.background.paper,
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, theme.palette.mode === "dark" ? 1 : 0.92),
  boxShadow: raised
    ? theme.palette.mode === "dark"
      ? `0 12px 30px ${alpha(theme.palette.common.black, 0.28)}`
      : `0 10px 26px ${alpha(theme.palette.common.black, 0.07)}`
    : "none",
});

export const tacticalAccordionSx = (theme) => ({
  mt: 1,
  borderRadius: 3,
  overflow: "hidden",
  ...tacticalSurface(theme, true),
  "&:before": { display: "none" },
  "&.Mui-disabled": {
    backgroundColor: theme.palette.background.paper,
    opacity: 1,
  },
});

export const tacticalSummarySx = (theme, tone = "neutral") => ({
  px: { xs: 1.5, sm: 2 },
  py: 0.35,
  minHeight: 58,
  backgroundColor: alpha(toneColor(theme, tone), theme.palette.mode === "dark" ? 0.1 : 0.045),
  borderBottom: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.9),
  "&.Mui-expanded": { minHeight: 58 },
  "& .MuiAccordionSummary-content": {
    my: 0.6,
    minWidth: 0,
    "&.Mui-expanded": { my: 0.6 },
  },
});

export const tacticalChipSx = (theme, tone = "neutral") => ({
  height: 24,
  borderRadius: 999,
  fontWeight: 700,
  px: 0.4,
  bgcolor: alpha(toneColor(theme, tone), theme.palette.mode === "dark" ? 0.16 : 0.08),
  color: theme.palette.text.primary,
  border: "1px solid",
  borderColor: alpha(toneColor(theme, tone), theme.palette.mode === "dark" ? 0.34 : 0.18),
});

export const tacticalActionSx = (theme, tone = "neutral", variant = "outlined") => {
  const color = toneColor(theme, tone);
  const base = {
    borderRadius: 1.6,
    minHeight: 36,
    px: 1.4,
    py: 0.7,
    textTransform: "none",
    fontWeight: 600,
    boxShadow: "none",
  };

  if (variant === "contained") {
    return {
      ...base,
      bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.22 : 0.14),
      color: theme.palette.text.primary,
      border: "1px solid",
      borderColor: alpha(color, theme.palette.mode === "dark" ? 0.38 : 0.2),
      "&:hover": {
        boxShadow: "none",
        bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.3 : 0.2),
        borderColor: alpha(color, theme.palette.mode === "dark" ? 0.46 : 0.28),
      },
    };
  }

  return {
    ...base,
    color: theme.palette.text.primary,
    border: "1px solid",
    borderColor: alpha(color, theme.palette.mode === "dark" ? 0.34 : 0.18),
    bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.08 : 0.03),
    "&:hover": {
      boxShadow: "none",
      borderColor: alpha(color, theme.palette.mode === "dark" ? 0.44 : 0.28),
      bgcolor: alpha(color, theme.palette.mode === "dark" ? 0.13 : 0.06),
    },
  };
};

export const tacticalMetricSx = (theme) => ({
  px: 1.15,
  py: 0.95,
  borderRadius: 1.65,
  border: "1px solid",
  borderColor: alpha(theme.palette.divider, 0.9),
  bgcolor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.035)
      : alpha(theme.palette.common.black, 0.02),
});

export const tacticalCardSx = (theme, interactive = false, selected = false, tone = "neutral") => {
  const color = toneColor(theme, tone);
  return {
    ...tacticalSurface(theme, false),
    borderRadius: 2.25,
    bgcolor: selected
      ? alpha(color, theme.palette.mode === "dark" ? 0.14 : 0.07)
      : theme.palette.background.paper,
    borderColor: selected
      ? alpha(color, theme.palette.mode === "dark" ? 0.42 : 0.24)
      : alpha(theme.palette.divider, 0.9),
    transition: interactive ? "all 0.16s ease" : undefined,
    cursor: interactive ? "pointer" : undefined,
    ...(interactive
      ? {
          "&:hover": {
            borderColor: alpha(color, theme.palette.mode === "dark" ? 0.5 : 0.3),
            backgroundColor: selected
              ? alpha(color, theme.palette.mode === "dark" ? 0.18 : 0.1)
              : alpha(color, theme.palette.mode === "dark" ? 0.08 : 0.03),
          },
        }
      : {}),
  };
};

export const tacticalFieldShellSx = (theme) => ({
  p: 1.1,
  borderRadius: 2.25,
  ...tacticalCardSx(theme, false),
  bgcolor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.03)
      : alpha(theme.palette.common.black, 0.015),
  height: "100%",
});

export const tacticalToolbarSx = (theme) => ({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: 1,
  p: 0.75,
  borderRadius: 999,
  ...tacticalSurface(theme, true),
  flexWrap: { xs: "wrap", sm: "nowrap" },
});

export const tacticalToggleGroupSx = (theme) => ({
  flex: 1,
  minWidth: 0,
  bgcolor:
    theme.palette.mode === "dark"
      ? alpha(theme.palette.common.white, 0.03)
      : alpha(theme.palette.common.black, 0.02),
  borderRadius: 999,
  p: 0.5,
  "& .MuiToggleButton-root": {
    border: 0,
    borderRadius: 999,
    px: { xs: 1.5, sm: 2 },
    py: 0.8,
    textTransform: "none",
    fontWeight: 600,
    whiteSpace: "nowrap",
    color: theme.palette.text.secondary,
    "&.Mui-selected": {
      color: theme.palette.text.primary,
      bgcolor: alpha(theme.palette.warning.main, theme.palette.mode === "dark" ? 0.18 : 0.1),
    },
  },
});

export const tacticalSelectorSx = (theme, selected = false, tone = "neutral") => {
  const color = toneColor(theme, tone);
  return {
    display: "inline-flex",
    alignItems: "center",
    gap: 0.65,
    px: 1.05,
    py: 0.55,
    borderRadius: 1.45,
    border: "1px solid",
    borderColor: selected
      ? alpha(color, theme.palette.mode === "dark" ? 0.5 : 0.3)
      : alpha(theme.palette.divider, 0.92),
    bgcolor: selected
      ? alpha(color, theme.palette.mode === "dark" ? 0.18 : 0.09)
      : theme.palette.background.paper,
    color: theme.palette.text.primary,
    cursor: "pointer",
    transition: "all 0.16s ease",
    "&:hover": {
      borderColor: alpha(color, theme.palette.mode === "dark" ? 0.58 : 0.36),
      bgcolor: selected
        ? alpha(color, theme.palette.mode === "dark" ? 0.24 : 0.12)
        : alpha(color, theme.palette.mode === "dark" ? 0.08 : 0.04),
    },
  };
};
